import { confirmToast } from "../../utils/toast";
import React, { useEffect, useState } from "react";
import axios from "../../utils/axiosConfig";
import { useLookup } from "../../utils/useLookup";
import { formatReportDate } from "../../utils/reportDate";

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "AED"];

const initialForm = {
  TxDate: "",
  ShipperID: "",
  Particular: "",
  Currency: "USD",
  Amount: "",
  CrDr: "Cr",
  Description: "",
};

const getErrorMessage = (error, fallback) => {
  const message = error.response?.data?.message || error.response?.data;
  return typeof message === "string" && message ? message : fallback;
};

const CreditDebit = () => {
  const { suppliers: shippers } = useLookup();
  const [activeTab, setActiveTab] = useState("list");
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [rows, setRows] = useState([]);
  const [filterCurrency, setFilterCurrency] = useState("USD");
  const [filterShipper, setFilterShipper] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  useEffect(() => {
    fetchList();
  }, []);

  const fetchList = async (params = {}) => {
    try {
      const res = await axios.get("/credit-debit/search", { params });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      setRows([]);
    }
  };

  const handleSearch = () =>
    fetchList({
      currency: filterCurrency,
      shipperId: filterShipper,
      fromDate: filterFrom,
      toDate: filterTo,
    });

  const handleClear = () => {
    setFilterCurrency("USD");
    setFilterShipper("");
    setFilterFrom("");
    setFilterTo("");
    fetchList();
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleEdit = async (id) => {
    try {
      const res = await axios.get(`/credit-debit/${id}`);
      const d = res.data;
      setForm({
        TxDate: d.TxDate ? d.TxDate.slice(0, 10) : "",
        ShipperID: d.ShipperID ?? "",
        Particular: d.Particular ?? "",
        Currency: d.Currency ?? "USD",
        Amount: d.Amount ?? "",
        CrDr: d.CrDr ?? "Cr",
        Description: d.Description ?? "",
      });
      setEditingId(id);
      setActiveTab("form");
    } catch (error) {
      alert(getErrorMessage(error, "Failed to load record."));
    }
  };

  const handleDelete = async (id) => {
    if (!await confirmToast("Delete this record?")) return;
    try {
      await axios.delete(`/credit-debit/${id}`);
      fetchList();
    } catch (error) {
      alert(getErrorMessage(error, "Failed to delete."));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.Description.trim()) {
      alert("Description is required.");
      return;
    }
    try {
      if (editingId) {
        await axios.put(`/credit-debit/${editingId}`, form);
      } else {
        await axios.post("/credit-debit", form);
      }
      alert(editingId ? "Credit/debit record updated successfully" : "Credit/debit record saved successfully");
      setForm(initialForm);
      setEditingId(null);
      setActiveTab("list");
      fetchList();
    } catch (error) {
      alert(getErrorMessage(error, "Failed to save."));
    }
  };

  const handleAddNew = () => {
    setForm(initialForm);
    setEditingId(null);
    setActiveTab("form");
  };

  const fmt = formatReportDate;

  return (
    <div className="container-fluid mt-3">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <span className="badge bg-success fs-6 px-3 py-2">Credit / Debit</span>
          {activeTab === "list" ? (
            <button className="btn btn-success btn-sm" onClick={handleAddNew}>+ Add New</button>
          ) : (
            <button className="btn btn-secondary btn-sm" onClick={() => { setActiveTab("list"); setForm(initialForm); setEditingId(null); }}>
              Back to List
            </button>
          )}
        </div>

        <div className="card-body">
          {activeTab === "list" ? (
            <>
              {/* Search Filters */}
              <div className="border rounded p-3 mb-3 bg-light">
                <div className="row g-2 align-items-end">
                  <div className="col-md-2">
                    <label className="form-label form-label-sm">Currency</label>
                    <select className="form-select form-select-sm" value={filterCurrency} onChange={(e) => setFilterCurrency(e.target.value)}>
                      <option value="">All</option>
                      {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label form-label-sm">Supplier</label>
                    <select className="form-select form-select-sm" value={filterShipper} onChange={(e) => setFilterShipper(e.target.value)}>
                      <option value="">All</option>
                      {shippers.map((s) => <option key={s.ShipperID} value={s.ShipperID}>{s.ShipperName}</option>)}
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label form-label-sm">From Date</label>
                    <input type="date" className="form-control form-control-sm" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label form-label-sm">To Date</label>
                    <input type="date" className="form-control form-control-sm" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
                  </div>
                  <div className="col-md-3 d-flex gap-2">
                    <button className="btn btn-success btn-sm" onClick={handleSearch}>Search</button>
                    <button className="btn btn-secondary btn-sm" onClick={handleClear}>Clear</button>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div style={{ overflowX: "auto" }}>
                <table className="table table-bordered table-hover table-sm" style={{ fontSize: "13px" }}>
                  <thead className="table-success">
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Supplier</th>
                      <th>Particular</th>
                      <th>Currency</th>
                      <th>Amount</th>
                      <th>Cr/Dr</th>
                      <th>Description</th>
                      <th>Action</th>
                      <th>Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={10} className="text-center text-muted py-3">No records found.</td></tr>
                    ) : rows.map((r, i) => (
                      <tr key={r.CreditDebitID} style={{ background: i % 2 === 0 ? "#fff" : "#f9f9f9" }}>
                        <td>{i + 1}</td>
                        <td>{fmt(r.TxDate)}</td>
                        <td>{r.ShipperName || "-"}</td>
                        <td>{r.Particular || "-"}</td>
                        <td>{r.Currency}</td>
                        <td>{r.Amount}</td>
                        <td>{r.CrDr}</td>
                        <td>{r.Description || "-"}</td>
                        <td>
                          <span className="text-primary" style={{ cursor: "pointer" }} onClick={() => handleEdit(r.CreditDebitID)}>Edit</span>
                        </td>
                        <td>
                          <span className="text-danger" style={{ cursor: "pointer" }} onClick={() => handleDelete(r.CreditDebitID)}>Delete</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            /* Add / Edit Form */
            <form onSubmit={handleSubmit}>
              <div className="row g-3" style={{ maxWidth: 600 }}>
                <div className="col-md-6">
                  <label className="form-label form-label-sm">Date <span className="text-danger">*</span></label>
                  <input type="date" className="form-control form-control-sm" required value={form.TxDate} onChange={(e) => set("TxDate", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-sm">Supplier <span className="text-danger">*</span></label>
                  <select className="form-select form-select-sm" required value={form.ShipperID} onChange={(e) => set("ShipperID", e.target.value)}>
                    <option value="">Select Supplier</option>
                    {shippers.map((s) => <option key={s.ShipperID} value={s.ShipperID}>{s.ShipperName}</option>)}
                  </select>
                </div>
                <div className="col-md-12">
                  <label className="form-label form-label-sm">Particular</label>
                  <input type="text" className="form-control form-control-sm" value={form.Particular} onChange={(e) => set("Particular", e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-sm">Currency</label>
                  <select className="form-select form-select-sm" value={form.Currency} onChange={(e) => set("Currency", e.target.value)}>
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-sm">Amount <span className="text-danger">*</span></label>
                  <input type="number" step="0.01" className="form-control form-control-sm" required value={form.Amount} onChange={(e) => set("Amount", e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label form-label-sm">Cr / Dr</label>
                  <select className="form-select form-select-sm" value={form.CrDr} onChange={(e) => set("CrDr", e.target.value)}>
                    <option value="Cr">Cr</option>
                    <option value="Dr">Dr</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label form-label-sm">Description <span className="text-danger">*</span></label>
                  <textarea
                    className="form-control form-control-sm"
                    required
                    maxLength={1000}
                    rows={3}
                    value={form.Description}
                    onChange={(e) => set("Description", e.target.value)}
                  />
                </div>
                <div className="col-12 d-flex gap-2">
                  <button type="submit" className="btn btn-success btn-sm px-4">
                    {editingId ? "Update" : "Save"}
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setActiveTab("list"); setForm(initialForm); setEditingId(null); }}>
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditDebit;
